// src/services/cashApi.js
import {
addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query,
  runTransaction, serverTimestamp, where, onSnapshot, startAfter} from "firebase/firestore";
import { db } from "../services/firebase";

/** Ouvre une session de caisse propre (pas de reliquats) */
export async function openCashSession({ locationId, openingFloat, openedBy }) {
  if (!openedBy) {
    throw new Error("Utilisateur (openedBy) manquant pour la session de caisse.");
  }

  // 🔁 Vérifie seulement si CET utilisateur a déjà une session ouverte à cet emplacement
  const openQ = query(
    collection(db, "cash_sessions"),
    where("locationId", "==", locationId),
    where("status", "==", "open"),
    where("openedBy", "==", openedBy),
    limit(1)
  );
  const openSnap = await getDocs(openQ);
  if (!openSnap.empty) {
    throw new Error("Tu as déjà une session de caisse ouverte pour cet emplacement.");
  }

  const ref = await addDoc(collection(db, "cash_sessions"), {
    locationId,
    openingFloat: Number(openingFloat || 0),
    openedBy: openedBy || "anonymous",
    openedAt: serverTimestamp(),
    status: "open",
  });

  await addDoc(collection(db, "cash_moves"), {
    sessionId: ref.id,
    locationId,
    type: "open_float",
    amount: Number(openingFloat || 0),
    source: "SYSTEM",
    note: "Fonds initial",
    at: serverTimestamp(),
  });

  const snap = await getDoc(ref);
  return { id: ref.id, ...snap.data() };
}

// Ajoute ceci dans cashApi.js
const CASH_MOVE_TYPES = new Set([
  "open_float",   // + montant initial
  "sale_in",      // + encaissement vente
  "change_out",   // - monnaie rendue (négatif)
  "adj_in",       // + ajustement manuel
  "adj_out",      // - ajustement manuel (négatif)
  "payout",       // - sortie (fournisseur, dépenses)
]);

export function validateCashMove({ type, amount }) {
  if (!CASH_MOVE_TYPES.has(type)) throw new Error(`Type de mouvement inconnu: ${type}`);
  const n = Number(amount || 0);
  if (!Number.isFinite(n)) throw new Error("Montant invalide.");
  if (type === "change_out" || type === "adj_out" || type === "payout") {
    if (n >= 0) throw new Error(`${type} doit être négatif.`);
  } else {
    if (n < 0) throw new Error(`${type} doit être positif.`);
  }
  return n;
}


/** Retourne la session ouverte pour un lieu (ou null) */
export async function findOpenSession(locationId, { cashierId = null } = {}) {
  const qy = query(
    collection(db, "cash_sessions"),
    where("locationId", "==", locationId),
    where("status", "==", "open"),
    orderBy("openedAt", "desc"),
    limit(10) // on prend un petit lot et on filtre côté JS
  );
  const snap = await getDocs(qy);
  if (snap.empty) return null;

  let docToUse = null;

  if (cashierId) {
    // On cherche la session ouverte par CETTE caissière
    docToUse = snap.docs.find(d => (d.data()?.openedBy === cashierId)) || null;
  } else {
    // Par défaut, on prend la plus récente
    docToUse = snap.docs[0];
  }

  if (!docToUse) return null;
  const d = docToUse;
  return { id: d.id, ...d.data() };
}

/** Enregistre un mouvement espèces pour CETTE session UNIQUEMENT */
export async function postCashMovement({ sessionId, locationId, type, amount, source, note }) {
  if (!sessionId) throw new Error("SessionId manquant pour le mouvement de caisse.");
  const n = validateCashMove({ type, amount });

  await addDoc(collection(db, "cash_moves"), {
    sessionId,
    locationId,
    type,
    amount: n,                 // signe validé
    source: source || "MANUAL",
    note: note || null,
    at: serverTimestamp(),
  });
}


/** Calcule le solde attendu d'une session en ne lisant QUE ses mouvements */
export async function expectedCash(sessionOrId) {
  if (!sessionOrId) return 0;
  const sessionId = typeof sessionOrId === "string" ? sessionOrId : sessionOrId.id;

  // Somme des mouvements de cette session
  const mvQ = query(
    collection(db, "cash_moves"),
    where("sessionId", "==", sessionId)
  );
  const mvSnap = await getDocs(mvQ);
  let total = 0;
  mvSnap.forEach(d => { total += Number(d.data()?.amount || 0); });

  return total; // NB: le "open_float" a été enregistré comme mouvement positif → il est déjà inclus
}

/** Flux LIVE du solde attendu (uniquement les mouvements de CETTE session).
 *  Retourne un unsubscribe (à appeler au unmount).
 */
export function expectedCashLive(sessionId, callback) {
  if (!sessionId) return () => {};
  const qy = query(
    collection(db, "cash_moves"),
    where("sessionId", "==", sessionId)
  );
  const unsub = onSnapshot(qy, (snap) => {
    let total = 0;
    snap.forEach((d) => { total += Number(d.data()?.amount || 0); });
    try { callback(total); } catch {}
  });
  return unsub;
}

/** Journal paginé des mouvements d’une session.
 *  @param {string} sessionId
 *  @param {{pageSize?:number, cursor?:any}} opts
 *  @returns {Promise<{items:Array, nextCursor:any|null}>}
 */
export async function listCashMoves(sessionId, { pageSize = 40, cursor = null } = {}) {
  if (!sessionId) return { items: [], nextCursor: null };
  let qy = query(
    collection(db, "cash_moves"),
    where("sessionId", "==", sessionId),
    orderBy("at", "desc"),
    limit(pageSize)
  );
  if (cursor) {
    qy = query(
      collection(db, "cash_moves"),
      where("sessionId", "==", sessionId),
      orderBy("at", "desc"),
      startAfter(cursor),
      limit(pageSize)
    );
  }
  const snap = await getDocs(qy);
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const nextCursor = snap.docs.length === pageSize ? snap.docs[snap.docs.length - 1] : null;
  return { items, nextCursor };
}


/** Ferme proprement la session (sans reliquats externes) */
export async function closeCashSession({ sessionId, closingCount, closedBy }) {
  const sRef = doc(db, "cash_sessions", sessionId);

  await runTransaction(db, async (tx) => {
    // LECTURE
    const sSnap = await tx.get(sRef);
    if (!sSnap.exists()) throw new Error("Session introuvable.");
    const s = sSnap.data();
    if (s.status !== "open") throw new Error("Session déjà fermée.");

    // (Pas de somme ici: on n'utilise pas de requête dans la transaction)
    // On écrit juste les infos de clôture.
    tx.set(sRef, {
      closedBy: closedBy || "anonymous",
      closedAt: serverTimestamp(),
      closingCount: Number(closingCount || 0),
      status: "closed",
    }, { merge: true });
  });
}
